import React, { useState, useEffect, useContext, useRef } from 'react'
import styled from 'styled-components'
import { LayoutContext, useUI } from '../../contexts'
import { SiteLink } from '../shared/SiteLink'
import { Icon } from '../shared/Icon'
import LogoSVG from '../graphics/Logo'
import AnnouncementV2 from './AnnouncementV2'

import {
  useNavClickedTracking,
  useSearchClickedTracking,
  useNavOpenedTracking,
  useNavClosedTracking,
} from '../../hooks/tracking'
import { useShopify } from '../../contexts/shopify'
import { getAvailable } from '../../utils'
import { useGlobal } from '../../hooks/useGlobal'
import { desktopBreakpointWidth } from '../../styles'
import AnnouncementSingle from './AnnouncementSingle'
import AnnouncementBlackFriday from './AnnouncementBlackFriday'

const Header = ({ className }, wrapperRef) => {
  const { storeSubdomain } = useContext(LayoutContext)
  const { checkouts, customDomains } = useShopify()
  const { lineItems } = checkouts[storeSubdomain]

  const {
    cartOpen,
    setCartOpen,
    searchOpen,
    sizeWizardOpen,
    setSearchOpen,
    setSizeWizardOpen,
    setAsideOpen,
    asideOpen,
  } = useUI()

  const lineItemsCount = lineItems.edges.reduce(
    (acc, { node: { quantity } }) => {
      return acc + quantity
    },
    0,
  )

  const {
    layoutType,
    node_locale,
    headerStyle: { color, bgColor, border, announcementVisible },
    currency,
  } = useContext(LayoutContext)

  const {
    cart: { cart },
    layout: {
      account,
      headerCenterLinks,
      headerRightMenu,
      showAnnouncementV2,
      announcementV2,
      announcementV2BgColor,
      announcementV2TextColor,
      announcementSingle,
      showAnnouncementSingle,
      announcementSingleBgColor,
      announcementSingleTextColor,
    },
    blackFriday: { showAnnouncementBarBlackFriday2022 },
  } = useGlobal(['cart', 'layout', 'blackFriday'], node_locale)

  const [style, setStyle] = useState({
    color,
    bgColor,
    border,
    announcementVisible,
  })
  const headerEl = useRef()
  const [prevOffset, setPrevOffset] = useState(0)

  const handleScroll = () => {
    if (
      typeof window !== 'undefined' &&
      (window.pageYOffset < 0 ||
        window.pageYOffset + window.innerHeight > document.body.clientHeight)
    ) {
      return
    }
    if (typeof window !== 'undefined' && !asideOpen && wrapperRef?.current) {
      const currentOffset = window.pageYOffset
      const scrollAmount = currentOffset - prevOffset
      const isScrollDown = currentOffset > prevOffset
      const wrapperEl = wrapperRef.current
      // In case the nav needs to be the only one sticky again (now both should be sticky) - uncomment line and value of announcementHeight
      // const announcementEl = wrapperEl.children[0]
      const announcementHeight = 0 // announcementEl.clientHeight
      const wrapperHeight = wrapperEl.clientHeight
      const alreadyTranslated = parseFloat(wrapperEl.dataset.translated)
      const translateBy = (amount) => {
        wrapperEl.style.transform = `translateY(-${amount}px)`
        wrapperEl.dataset.translated = amount
      }

      if (!announcementVisible && wrapperEl) {
        wrapperEl.classList.add('fixed')
        translateBy(announcementHeight)
        document.body.style.paddingTop = 0
        wrapperEl.dataset.translated = announcementHeight
        return
      } else if (currentOffset > announcementHeight) {
        wrapperEl.classList.remove('initial')
        wrapperEl.classList.add('fixed')
        document.body.style.paddingTop = `${wrapperHeight}px`
        translateBy(announcementHeight)

        if (isScrollDown) {
          if (
            (prevOffset <= announcementHeight &&
              currentOffset > announcementHeight) ||
            scrollAmount >= announcementHeight ||
            (scrollAmount < announcementHeight &&
              scrollAmount + alreadyTranslated >= announcementHeight)
          ) {
            translateBy(announcementHeight)
          } else {
            translateBy(alreadyTranslated + scrollAmount)
          }
        }

        if (!isScrollDown) {
          if (
            alreadyTranslated <= announcementHeight &&
            -scrollAmount < announcementHeight &&
            -scrollAmount < alreadyTranslated
          ) {
            translateBy(alreadyTranslated + scrollAmount)
          } else {
            translateBy(0)
          }
        }
      } else if (isScrollDown || (!isScrollDown && currentOffset === 0)) {
        wrapperEl.classList.remove('fixed')
        translateBy(0)
        wrapperEl.dataset.translated = 0
        document.body.style.paddingTop = 0
      } else if (currentOffset < announcementHeight) {
        translateBy(0)
      } else {
        translateBy(alreadyTranslated + scrollAmount)
      }
      setPrevOffset(currentOffset)
    }
  }

  useEffect(() => {
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  })

  useEffect(() => {
    const updateBgColor = () => {
      if (window.scrollY === 0) {
        setStyle({
          color,
          bgColor,
          border,
          announcementVisible,
        })
      } else {
        setStyle({
          color: 'black',
          bgColor: 'white',
          border: true,
          announcementVisible: false,
        })
      }
    }
    if (!announcementVisible) {
      window.addEventListener('scroll', updateBgColor)
    }

    setTimeout(() => {
      updateBgColor()
    }, 50)

    return () => window.removeEventListener('scroll', updateBgColor)
  }, [color, bgColor, border, announcementVisible])

  if (layoutType && layoutType.singleHeaderButton) {
    return (
      <StyledHeader
        showAnnouncement={style.announcementVisible}
        className={'fixed'}
        color={style.color}
        bgColor={style.bgColor}
        border={style.border}
        open={asideOpen || cartOpen}
      >
        <Logo
          onClick={() => {
            if (cartOpen || asideOpen || sizeWizardOpen || searchOpen) {
              setCartOpen(false)
              setSizeWizardOpen(false)
              setSearchOpen(false)
              setAsideOpen(false)
            }
            useNavClickedTracking({
              currency: currency?.code,
              locale: node_locale,
              label: 'Logo',
              linkType: 'Button',
              navType: 'Header left',
              destination: '/',
            })
          }}
          // for page tracking referrer prop
          state={{
            referrer: typeof window !== 'undefined' && window.location.pathname,
          }}
          isopened={cartOpen ? cartOpen : undefined}
          layouttype={'landingpage'}
        >
          <LogoSVG />
        </Logo>
        <MobileCartLogo to='' isopened={cartOpen ? cartOpen : undefined}>
          <span>{cart}</span>
        </MobileCartLogo>
        <HeaderButton
          to={layoutType.singleHeaderButton.target.slug}
          extension={layoutType.singleHeaderButton.linkExtension}
          onClick={() =>
            useNavClickedTracking({
              currency: currency?.code,
              locale: node_locale,
              label: layoutType.singleHeaderButton.title,
              linkType: 'Button',
              navType: 'Header Single Button',
              destination: `/${node_locale}/${layoutType.singleHeaderButton.target.slug}/`,
            })
          }
          state={{
            referrer: typeof window !== 'undefined' && window.location.pathname,
          }}
        >
          {layoutType.singleHeaderButton.title}
        </HeaderButton>
      </StyledHeader>
    )
  }

  return (
    <>
      <Wrapper
        ref={wrapperRef}
        data-translated={0}
        className={announcementVisible ? 'initial' : 'fixed'}
      >
        {showAnnouncementV2 && announcementV2 && (
          <AnnouncementV2
            announcement={announcementV2}
            announcementVisible={announcementVisible}
            announcementBgColor={announcementV2BgColor}
            announcementTextColor={announcementV2TextColor}
          />
        )}
        <StyledHeader
          ref={headerEl}
          showAnnouncement={style.border}
          color={style.color}
          bgColor={style.bgColor}
          border={style.border}
          open={asideOpen || cartOpen}
        >
          <HamburgerMenuButton
            type='button'
            aria-label='open menu'
            onClick={() => {
              if (cartOpen || asideOpen) {
                setCartOpen(false)
                setAsideOpen(false)
                useNavClosedTracking({
                  currency: currency?.code,
                  locale: node_locale,
                  category: 'Menu level 1',
                  navType: 'Directory blade',
                })
              } else {
                setAsideOpen(true)
                useNavOpenedTracking({
                  currency: currency?.code,
                  locale: node_locale,
                  category: 'Menu level 1',
                  navType: 'Directory blade',
                })
              }
              setSearchOpen(false)
            }}
            asideOpen={asideOpen}
            cartOpen={cartOpen}
          >
            <div className='color-bg' />
            <div className='color-bg' />
            <div className='color-bg' />
          </HamburgerMenuButton>

          <Logo
            onClick={() => {
              if (cartOpen || asideOpen || sizeWizardOpen || searchOpen) {
                setCartOpen(false)
                setAsideOpen(false)
                setSizeWizardOpen(false)
                setSearchOpen(false)
              }
              useNavClickedTracking({
                currency: currency?.code,
                locale: node_locale,
                label: 'Logo',
                linkType: 'Button',
                navType: 'Header left',
                destination: '/',
              })
            }}
            aria-label='Stox Energy Logo'
            state={{
              referrer:
                typeof window !== 'undefined' && window.location.pathname,
            }}
            isopened={cartOpen ? cartOpen : undefined}
          >
            <LogoSVG />
          </Logo>
          <MobileCartLogo to='' isopened={cartOpen ? cartOpen : undefined}>
            <span>{cart}</span>
          </MobileCartLogo>
          <CenterLinkList>
            {headerCenterLinks.filter(getAvailable).map((link) => (
              <li key={link.id}>
                <SiteLink
                  to={link.target && link.target.slug}
                  extension={link.linkExtension}
                  onClick={() => {
                    setSearchOpen(false)
                    useNavClickedTracking({
                      currency: currency?.code,
                      locale: node_locale,
                      label: link.title,
                      linkType: 'Link',
                      navType: 'Header center',
                      destination: `/${node_locale}/${link.target.slug}/`,
                    })
                  }}
                  state={{
                    referrer:
                      typeof window !== 'undefined' && window.location.pathname,
                  }}
                >
                  <CenterLink>{link.title}</CenterLink>
                </SiteLink>
              </li>
            ))}
          </CenterLinkList>
          <RightFlexContainer>
            <RightLinksList>
              {node_locale === 'nl'
                ? headerRightMenu.map((link) => (
                    <li key={link.id}>
                      <SiteLink
                        to={link.target && link.target.slug}
                        extension={link.linkExtension}
                        onClick={() => {
                          setSearchOpen(false)
                          useNavClickedTracking({
                            currency: currency?.code,
                            locale: node_locale,
                            label: link.title,
                            linkType: 'Link',
                            navType: 'Header center',
                            destination: `/${node_locale}/${link.target.slug}/`,
                          })
                        }}
                        state={{
                          referrer:
                            typeof window !== 'undefined' &&
                            window.location.pathname,
                        }}
                      >
                        {link.title}
                      </SiteLink>
                    </li>
                  ))
                : headerRightMenu
                    .filter(
                      ({ target }) => target && target.slug !== 'store-locator',
                    )
                    .map((link) => (
                      <li key={link.id}>
                        <SiteLink
                          to={link.target && link.target.slug}
                          extension={link.linkExtension}
                          onClick={() => {
                            setSearchOpen(false)
                            useNavClickedTracking({
                              currency: currency?.code,
                              locale: node_locale,
                              label: link.title,
                              linkType: 'Link',
                              navType: 'Header center',
                              destination: `/${node_locale}/${link.target.slug}/`,
                            })
                          }}
                          state={{
                            referrer:
                              typeof window !== 'undefined' &&
                              window.location.pathname,
                          }}
                        >
                          {link.title}
                        </SiteLink>
                      </li>
                    ))}
              <li>
                <a
                  href={`https://${customDomains[storeSubdomain]}/${
                    !node_locale.includes('en') ? `${node_locale}/` : ''
                  }account/`}
                >
                  {account}
                </a>
              </li>
            </RightLinksList>
            <Buttons>
              <SearchButton
                type='button'
                aria-label={`${searchOpen ? 'open' : 'close'} search`}
                onClick={() => {
                  if (searchOpen) {
                    setSearchOpen(false)
                  } else {
                    setSearchOpen(true)
                    useSearchClickedTracking({
                      currency: currency?.code,
                      locale: node_locale,
                      label: 'Search icon',
                      category: 'Search',
                    })
                  }
                  setCartOpen(false)
                  setAsideOpen(false)
                }}
              >
                <Icon
                  name={
                    // Show the close icon on mobile
                    typeof window !== 'undefined' &&
                    window.innerWidth < desktopBreakpointWidth &&
                    searchOpen
                      ? 'close'
                      : 'search'
                  }
                />
              </SearchButton>
              <CartButton
                type='button'
                aria-label='open cart'
                onClick={() => {
                  if (cartOpen) {
                    setCartOpen(false)
                  } else {
                    setCartOpen(true)
                  }
                  setSearchOpen(false)
                }}
              >
                <Icon name='cart' />
                <CartAmount className='color' items={lineItemsCount > 0}>
                  {lineItemsCount}
                </CartAmount>
              </CartButton>
            </Buttons>
          </RightFlexContainer>
        </StyledHeader>
        {/* Black Friday has priority over usual announcement bar */}
        {showAnnouncementBarBlackFriday2022 && <AnnouncementBlackFriday />}
        {showAnnouncementSingle &&
          announcementSingle &&
          announcementVisible && (
            <AnnouncementSingle
              announcement={announcementSingle}
              announcementBgColor={announcementSingleBgColor}
              announcementTextColor={announcementSingleTextColor}
            />
          )}
      </Wrapper>
    </>
  )
}

const Wrapper = styled.div`
  z-index: 999999;
  position: relative;
  width: 100%;
  &.fixed {
    top: 0;
    position: fixed;
  }
  &.initial {
    transform: translateY(0) !important;
  }
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    z-index: 99;
  }
`

const StyledHeader = styled.section`
  position: relative;
  height: ${(props) => props.theme.headerHeight.mobile};
  width: 100%;
  top: 0;
  display: flex;
  align-items: center;
  background: ${(props) => (props.open ? 'white' : props.bgColor)};
  color: ${(props) => (props.open ? 'black' : props.color)};

  /* border-bottom: 1px solid
    ${(props) => (props.border ? '#e5e5e5' : 'transparent')}; */

  border-bottom: 1px solid
    ${(props) =>
      props.border || props.showAnnouncement ? '#e5e5e5' : 'transparent'};
  &.fixed {
    position: fixed;
    z-index: 99;
  }

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    color: ${(props) => props.color};
    height: ${(props) => props.theme.headerHeight.desktop};
    background: ${(props) => props.bgColor};
    transition: all 0.2s ease-in-out;
    top: 0;
    left: 0;
    right: 0;
  }

  svg {
    fill: ${(props) => (props.open ? 'black' : props.color)};

    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      fill: ${(props) => props.color};
    }
  }

  .color {
    color: ${(props) => (props.open ? 'black' : props.color)};

    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      color: ${(props) => props.color};
    }
  }

  .color-bg {
    background: ${(props) => (props.open ? 'black' : props.color)};

    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      background: ${(props) => props.color};
    }
  }

  ${(props) =>
    props.color === 'transparent' &&
    `
    background-color:  transparent;
    `}
`

const HamburgerMenuButton = styled.button`
  height: ${(props) => props.theme.space.marginMobile * 1.2}vw;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  margin: ${(props) => props.theme.space.marginMobile}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    display: none;
  }

  div {
    height: ${(props) => props.theme.space.marginMobile / 8}vw;
    width: ${(props) => props.theme.space.marginMobile * 1.5}vw;
    background: black;
  }

  ${(props) =>
    (props.asideOpen || props.cartOpen) &&
    `
  div {
    &:first-child {
      transform: translate3d(
          0,
          ${props.theme.space.marginMobile / 2}vw,
          0
        )
        rotate(45deg);
    }

    &:nth-child(2) {
      display: none;
    }

    &:nth-child(3) {
      transform: translate3d(0, -2.3vw, 0) rotate(-45deg);
    }
  }
  `}
`

const Logo = styled(SiteLink)`
  display: flex;
  text-decoration: none;
  align-items: center;
  z-index: 3;
  margin-left: ${(props) => (props.layouttype === 'landingpage' ? `4vw` : '')};
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding: 0 ${(props) => props.theme.space.marginDesktop * 4}vw;
  }

  @media (max-width: ${(props) => props.theme.breakPoints.desktop}) {
    ${(props) => (props.isopened ? 'display: none;' : '')}
  }

  svg {
    height: ${(props) => props.theme.space.marginMobile * 1.2}vw;
    padding-left: ${(props) => props.theme.space.marginMobile / 5}vw;

    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      height: 1.8vw;
      padding-left: 0;
    }

    @media screen and (-ms-high-contrast: active) and (min-width: ${(props) =>
        props.theme.breakPoints.desktop}),
      screen and (-ms-high-contrast: none) and (min-width: ${(props) =>
        props.theme.breakPoints.desktop}) {
      width: 14vw;
    }
  }

  span {
    height: ${(props) => props.theme.space.marginMobile / 1.5}vw;
    width: 1px;
    background-color: black;
    opacity: 0.5;
    margin-left: ${(props) => props.theme.space.marginMobile / 4}vw;
    margin-right: ${(props) => props.theme.space.marginMobile / 3}vw;

    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      height: ${(props) => props.theme.space.marginDesktop * 1.5}vw;
      width: 1px;
      margin-left: 0.5vw;
      margin-right: 0.5vw;
    }
  }

  p {
    font-size: ${(props) => props.theme.fontSize.m20};
    color: inherit;
    opacity: 0.5;
    white-space: nowrap;
    letter-spacing: 0.1em;
    line-height: 0.8;

    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      font-size: ${(props) => props.theme.fontSize.d12};
    }
  }
`

const MobileCartLogo = styled(SiteLink)`
  display: none;

  @media (max-width: ${(props) => props.theme.breakPoints.desktop}) {
    ${(props) => (props.isopened ? 'display: block;' : '')}
  }
`

const CenterLinkList = styled.ul`
  display: none;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    display: flex;
    font-family: ${(props) => props.theme.fontFamily.bold};
    text-transform: uppercase;
    font-size: ${(props) => props.theme.fontSize.d12};
    letter-spacing: 0.15em;
    width: 100%;
    text-align: center;
    justify-content: center;
    position: absolute;
    height: ${(props) => props.theme.headerHeight.desktop};
    top: 0;
    left: 0;

    li {
      cursor: pointer;
      margin: 0 ${(props) => props.theme.space.marginDesktop}vw;
      display: flex;
      align-items: center;
      padding: ${(props) => props.theme.space.marginDesktop}vw 0;
    }
  }
`
const CenterLink = styled.div`
  padding: ${(props) => props.theme.space.marginDesktop}vw 0;
  position: relative;
  &::after {
    content: '';
    width: 100%;
    display: block;
    position: absolute;
    border-bottom: ${(props) => props.theme.space.marginDesktop / 8}vw solid
      ${(props) => props.theme.colors.blue};
    height: ${(props) => props.theme.space.marginDesktop / 2}vw;
    transform: ${(props) => (props.active ? 'scaleX(1)' : 'scaleX(0)')};
    transition: none;
    transform-origin: left;
    will-change: transform;
  }
  &:hover {
    &::after {
      transform: scaleX(1);
      transition: transform 0.2s ease-out;
    }
  }
`
const RightFlexContainer = styled.div`
  position: absolute;
  right: 0;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    display: flex;
    align-items: center;
    margin-right: ${(props) => props.theme.space.marginDesktop * 4.8}vw;
    z-index: 3;
  }
  @media screen and (-ms-high-contrast: active) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}),
    screen and (-ms-high-contrast: none) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}) {
    top: 1.4vw;
  }
`

const RightLinksList = styled.ul`
  display: none;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    display: flex;
    font-family: ${(props) => props.theme.fontFamily.normal};
    text-transform: uppercase;
    font-size: 0.75vw;
    letter-spacing: 0.15em;
    margin-right: ${(props) => props.theme.space.marginDesktop / 2}vw;

    a {
      cursor: pointer;
      margin: 4px ${(props) => props.theme.space.marginDesktop / 2}vw 0;
      display: inline-block;
      ${(props) => props.theme.underline('#000', '0.05em')}
    }
  }

  @media screen and (-ms-high-contrast: active) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}),
    screen and (-ms-high-contrast: none) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}) {
    top: 43%;
  }
`

const Buttons = styled.div`
  display: flex;

  @media screen and (-ms-high-contrast: active),
    screen and (-ms-high-contrast: none) {
    top: 30%;
  }
`

const SearchButton = styled.button`
  margin-right: ${(props) => props.theme.space.marginMobile * 0.9}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    margin: ${(props) => props.theme.space.marginDesktop * 2}vw
      ${(props) => props.theme.space.marginDesktop}vw
      ${(props) => props.theme.space.marginDesktop * 2}vw 0;
    cursor: pointer;
  }

  svg {
    height: ${(props) => props.theme.space.marginMobile * 1.6}vw;
    width: ${(props) => props.theme.space.marginMobile * 1.6}vw;

    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      height: ${(props) => props.theme.space.marginDesktop * 1.8}vw;
      width: ${(props) => props.theme.space.marginDesktop * 1.8}vw;
    }
  }
`

const CartButton = styled.button`
  position: relative;
  margin-right: ${(props) => props.theme.space.marginMobile}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    margin: ${(props) => props.theme.space.marginDesktop * 2}vw 0;
    cursor: pointer;
  }

  svg {
    height: ${(props) => props.theme.space.marginMobile * 1.5}vw;
    width: ${(props) => props.theme.space.marginMobile * 1.5}vw;

    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      height: ${(props) => props.theme.space.marginDesktop * 1.6}vw;
      width: ${(props) => props.theme.space.marginDesktop * 1.6}vw;
    }
  }
`

const CartAmount = styled.div`
  font-size: ${(props) => props.theme.fontSize.m20};
  position: absolute;
  top: 60%;
  left: 50%;
  transform: translate3d(-50%, -60%, 0);
  font-family: ${(props) =>
    props.items ? 'MaisonNeueBold' : 'MaisonNeueBook'};

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d12};
    top: 40%;
    left: 0;
    right: -2.1vw;
    transform: translate3d(0, -40%, 0);
    font-family: ${(props) => props.theme.fontFamily.bold};
  }

  @media screen and (-ms-high-contrast: active) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}),
    screen and (-ms-high-contrast: none) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}) {
    font-size: 0.6vw;
    top: 0.45vw;
    right: 0;
    transform: translate3d(0, 0, 0);
  }
`

const HeaderButton = styled(SiteLink)`
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  background-color: ${(props) => props.theme.colors.blue};
  color: #fff;
  font-size: 2.8vw;
  text-decoration: none;
  padding: ${(props) => props.theme.space.marginMobile * 0.8}vw
    ${(props) => props.theme.space.marginMobile * 1}vw;
  font-family: ${(props) => props.theme.fontFamily.medium};
  letter-spacing: 0.05em;
  margin-left: auto;
  margin-right: ${(props) => props.theme.space.marginMobile * 1.3}vw;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    margin-right: ${(props) => props.theme.space.marginDesktop * 4}vw;
    font-size: 0.85vw;
    padding: ${(props) => props.theme.space.marginDesktop * 2}vw
      ${(props) => props.theme.space.marginDesktop * 4}vw;
  }
`

export default React.forwardRef(Header)
